import { useMutation } from '@apollo/client';

import { gql } from '../gql-tag';

export const taskFieldsFragment = gql(`
  fragment TaskFields on TaskQL {
    id
    engagementId
    title
    status
    engagementId
    completedAt
    completedByParticipant {
      displayName
    }
    completionSource
    dismissedAt
    dismissedByParticipant {
      displayName
    }
    dismissalSource
    recommendedActions {
      ...TaskActionFields
    }
    
  }
`);

export const taskActionsFragment = gql(`
  fragment TaskActionFields on TaskActionQL {
      type
      actionData
  }
`);

export const engagementTasksFragment = gql(`
  fragment EngagementTasks on EngagementQL {
    tasks {
      ...TaskFields
    }
  }
`);

export const completeTaskMutation = gql(`
  mutation completeTask($input: CompleteTaskInput!) {
    completeTask(input: $input) {
      task {
        id
        status
        completedAt
        completedByParticipant {
          displayName
        }
      }
    }
  }
`);

export const dismissTaskMutation = gql(`
  mutation dismissTask($input: DismissTaskInput!) {
    dismissTask(input: $input) {
      task {
        id
        status
        dismissedAt
        dismissalReason
        dismissedByParticipant {
          displayName
        }
      }
    }
  }
`);

export const createTaskForEngagementMutation = gql(`
  mutation createTaskForEngagement($input: NewTaskForEngagementInput!) {
    createTaskForEngagement(input: $input) {
      task {
        ...TaskFields
      }
    }
  }
`);

export const resetTaskStatusMutation = gql(`
  mutation resetTaskStatus($input: ResetTaskStatusInput!) {
    resetTaskStatus(input: $input) {
      task {
        id
        status
      }
    }
  }
`);

export const useCompleteTask = () => {
  return useMutation(completeTaskMutation, {
    optimisticResponse: vars => ({
      completeTask: {
        __typename: 'CompleteTask' as const,
        task: {
          __typename: 'TaskQL' as const,
          id: vars.input.taskId,
          status: 'completed',
          completedAt: new Date().toISOString(),
          completedByParticipant: {
            __typename: 'ParticipantQL' as const,
            displayName: 'You',
          },
        },
      },
    }),
  });
};

export const useCreateTaskForEngagement = () => {
  return useMutation(createTaskForEngagementMutation, {
    optimisticResponse: vars => ({
      createTaskForEngagement: {
        __typename: 'CreateTaskForEngagement' as const,
        task: {
          __typename: 'TaskQL' as const,
          id: `temp-id-${Date.now()}`,
          title: vars.input.title,
          status: 'todo',
          engagementId: vars.input.engagementId,
          completedAt: null,
          completedByParticipant: null,
          completionSource: null,
          dismissedAt: null,
          dismissedByParticipant: null,
          dismissalSource: null,
        },
      },
    }),
    update: (cache, { data }) => {
      if (!data?.createTaskForEngagement?.task) return;

      const newTask = data.createTaskForEngagement.task;
      const engagementId = `EngagementQL:${newTask.engagementId}`;

      cache.updateFragment(
        {
          id: engagementId,
          fragment: engagementTasksFragment,
          fragmentName: 'EngagementTasks',
        },
        cachedData => {
          if (!cachedData) return;

          const taskExists = cachedData.tasks?.some(
            task => task.id === newTask.id,
          );
          if (taskExists) return cachedData;

          return {
            ...cachedData,
            tasks: [...cachedData.tasks, newTask],
          };
        },
      );
    },
  });
};

export const useDismissTask = () => {
  return useMutation(dismissTaskMutation);
};

export const useResetTaskStatus = () => {
  return useMutation(resetTaskStatusMutation, {
    optimisticResponse: vars => ({
      resetTaskStatus: {
        __typename: 'ResetTaskStatus' as const,
        task: {
          __typename: 'TaskQL' as const,
          id: vars.input.taskId,
          status: 'todo',
        },
      },
    }),
  });
};
